import { c as _c } from "react/compiler-runtime";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { SimpleHero } from "../terra/heroes/SimpleHero/SimpleHero";
import { HeroLead } from "../terra/heroes/HeroLead/HeroLead";
import { HeroSearchButton } from "../terra/heroes/HeroLead/HeroSearchButton";
import { normalToMachineTheme, handleButtons } from "@/lib/utils";
import { mapContentfulVideo, type MappedHeroVideo } from "@/components/terra/utils/mapContentfulVideo";
import { resolveHeroHeaderSize, type HeroHeaderSize } from "@/lib/heroHeaderSize";
export { HERO_SECTION_TYPES, findPromotedHeaderIndex, heroHasHeading, isHeroSection, resolveHeaderElement } from "./MXPFlexibleHero.pageHeader";
interface FlexibleHeroPanel {
  eyebrow?: string;
  header?: string;
  subheader?: string;
  description?: string;
  presetTheme?: string;
  overlayOption?: string;
  headerSize?: HeroHeaderSize;
  isWrapped?: boolean;
  isSearchEnabled?: boolean;
  searchPlaceholderText?: string;
  searchFilter?: string;
  searchIndex?: string;
  buttons?: any[];
  backgroundImage?: any;
  imageConfiguration?: string;
  hideImageOnMobile?: boolean;
}
function mapPanel(item: any, searchEnabled: boolean): FlexibleHeroPanel {
  let backgroundImg = null;
  // "No Image" hides the media entirely; any other configuration renders the
  // background image when one is present.
  if (item?.imageConfiguration !== "No Image" && item?.media?.image?.url) {
    backgroundImg = {
      title: item?.media?.altText,
      altText: item?.media?.altText,
      height: item?.media?.image?.height,
      width: item?.media?.image?.width,
      url: item?.media?.image?.url,
      focusArea: item?.media?.focusArea
    };
  }
  return {
    eyebrow: item?.eyebrow,
    header: item?.header,
    subheader: item?.subheader,
    description: documentToHtmlString(item?.description?.json),
    presetTheme: normalToMachineTheme(item?.theme),
    overlayOption: item?.overlayOption?.toLowerCase() || "none",
    isWrapped: true,
    headerSize: resolveHeroHeaderSize(item?.headerSizing, item?.header),
    // Search state follows the heroStyle toggle, never the per-panel flag.
    isSearchEnabled: searchEnabled,
    searchPlaceholderText: item?.searchPlaceholderText,
    searchFilter: item?.searchFilter,
    searchIndex: item?.searchIndex,
    buttons: handleButtons(item?.buttonsCollection?.items || []),
    backgroundImage: backgroundImg,
    imageConfiguration: item?.imageConfiguration,
    hideImageOnMobile: item?.hideImageOnMobile || false
  };
}
export default function MXPFlexibleHero(props) {
  const $ = _c(48);
  const {
    index,
    value,
    headerElement,
    isLCP,
    breadcrumbs,
    breadcrumbDarkMode
  } = props;
  if (value === undefined || value === null) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <div className="text-red-500 p-8 text-center">ERROR</div>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  const t0 = value?.heroStyle || "Simple";
  let isLeadStyle;
  let searchEnabled;
  let t1;
  let video;
  if ($[1] !== t0 || $[2] !== value?.contentCollection?.items || $[3] !== value?.video) {
    const style = t0.trim();
    isLeadStyle = style.startsWith("Lead");
    searchEnabled = style.endsWith("with Search");
    const t2 = value?.video;
    let t3;
    if ($[8] !== t2) {
      t3 = mapContentfulVideo(t2);
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    video = t3;
    t1 = (value?.contentCollection?.items || []).filter(Boolean).map(item => mapPanel(item, searchEnabled));
    $[1] = t0;
    $[2] = value?.contentCollection?.items;
    $[3] = value?.video;
    $[4] = isLeadStyle;
    $[5] = searchEnabled;
    $[6] = t1;
    $[7] = video;
  } else {
    isLeadStyle = $[4];
    searchEnabled = $[5];
    t1 = $[6];
    video = $[7];
  }
  const panels = t1;
  if (isLeadStyle) {
    const t2 = value?.bottomTheme;
    const t3 = value?.componentImagePriority;
    const t4 = !!value?.forceFullHeightVideo;
    const t5 = index === 0;
    let t6;
    if ($[10] !== breadcrumbDarkMode || $[11] !== breadcrumbs || $[12] !== headerElement || $[13] !== index || $[14] !== isLCP || $[15] !== panels || $[16] !== searchEnabled || $[17] !== t2 || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== video) {
      t6 = <><HeroLead key={index} presetTheme={t2} heroStyle="lead" content={panels} isSearchEnabled={searchEnabled} componentImagePriority={t3} headerElement={headerElement} isLCP={isLCP} video={video} forceFullHeightVideo={t4} showScrollToContent={t5} breadcrumbs={breadcrumbs} breadcrumbDarkMode={breadcrumbDarkMode} /></>;
      $[10] = breadcrumbDarkMode;
      $[11] = breadcrumbs;
      $[12] = headerElement;
      $[13] = index;
      $[14] = isLCP;
      $[15] = panels;
      $[16] = searchEnabled;
      $[17] = t2;
      $[18] = t3;
      $[19] = t4;
      $[20] = t5;
      $[21] = video;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    return t6;
  }
  const panel = panels[0];
  const t2 = panel?.eyebrow;
  const t3 = panel?.header;
  const t4 = panel?.subheader;
  const t5 = panel?.description;
  const t6 = panel?.presetTheme as any;
  const t7 = panel?.overlayOption as any;
  const t8 = panel?.headerSize;
  const t9 = panel?.isWrapped;
  const t10 = panel?.buttons;
  const t11 = panel?.backgroundImage;
  const t12 = value?.componentImagePriority;
  const t13 = panel?.imageConfiguration;
  const t14 = panel?.hideImageOnMobile;
  let t15;
  if ($[23] !== panel?.searchFilter || $[24] !== panel?.searchIndex || $[25] !== panel?.searchPlaceholderText || $[26] !== searchEnabled) {
    t15 = searchEnabled ? <HeroSearchButton label={panel?.searchPlaceholderText} searchFilter={panel?.searchFilter} searchIndex={panel?.searchIndex} /> : undefined;
    $[23] = panel?.searchFilter;
    $[24] = panel?.searchIndex;
    $[25] = panel?.searchPlaceholderText;
    $[26] = searchEnabled;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== breadcrumbDarkMode || $[29] !== breadcrumbs || $[30] !== headerElement || $[31] !== index || $[32] !== isLCP || $[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t13 || $[37] !== t14 || $[38] !== t15 || $[39] !== t2 || $[40] !== t3 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8 || $[46] !== t9) {
    t16 = <><SimpleHero key={index} eyebrow={t2} header={t3} headerElement={headerElement} subheader={t4} description={t5} presetTheme={t6} overlayOption={t7} headerSize={t8} isWrapped={t9} buttons={t10} backgroundImage={t11} componentImagePriority={t12} isLCP={isLCP} imageConfiguration={t13} hideImageOnMobile={t14} breadcrumbs={breadcrumbs} breadcrumbDarkMode={breadcrumbDarkMode} searchComponent={t15} /></>;
    $[28] = breadcrumbDarkMode;
    $[29] = breadcrumbs;
    $[30] = headerElement;
    $[31] = index;
    $[32] = isLCP;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t2;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
}